import { ColorTypes } from '@genial/design-tokens';
import { BackendError, Service } from '@genial/services';
// TODO: Properly export meta response type
import { MetaResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { selectorFamily } from 'recoil';

import { FiltersToApplyProps } from '../../hooks/useFilterTransformer';
import { blankMetaResponse } from '../../utils/blankMetaResponse';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';

import { screenName } from './constants';
import { normalizeFilterResponse, normalizeFilters } from './normalize';
import { AbstractProductsQueryData } from './types';

export type FixedIncomeProductsQueryItem = Utils.ArrayElement<
  NonNullable<
    Awaited<
      ReturnType<typeof Service.ReportingServices.V1.getReportingProductListingFixedIncome>
    >['data']
  >
> & {
  color: ColorTypes.Option;
  customerNormalized: string;
  issuerNormalized: string;
  productNameNormalized: string;
  indexNormalized: string;
  blockedQuantityNormalized: number;
  totalQuantityNormalized: number;
  grossValueNormalized: string;
  assetNormalized: string;
  positionDateNormalized: string;
  maturityDateNormalized: string;
  maturityDaysRemainingNormalized: string;
  purchaseDateNormalized: string;
  purchaseDaysRemainingNormalized: string;
  iofValueNormalized: string;
  irValueNormalized: string;
  purchaseValueNormalized: string;
  netValueNormalized: string;
  formattedIndexRateNormalized: string;
};

export type FixedIncomeProductsQueryData = AbstractProductsQueryData<FixedIncomeProductsQueryItem>;

export const fixedIncomeProductsQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: FixedIncomeProductsQueryData;
    }
  | undefined,
  { page: number; searchTerm?: string; year: number; filtersToApply: FiltersToApplyProps }
>({
  key: 'ManagementAnalysisDetails/fixedIncomeProductsQuery',
  get:
    ({ page, searchTerm, filtersToApply, year }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } =
          await Service.ReportingServices.V1.getReportingProductListingFixedIncome({
            screenName,
            page,
            searchTerm,
            year,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            ...filtersToApply,
          });

        const normalizedData = data?.map(
          (item) =>
            ({
              ...item,
              color: 'product.rf',
              customerNormalized: item.customer || '',
              issuerNormalized: item.issuer || '',
              productNameNormalized: item.productName || '',
              assetNormalized: item.asset || '',
              positionDateNormalized: Utils.Mask.DateFormat(item.positionDate, 'dd/MM/yyyy'),
              indexNormalized: item.index || '',
              blockedQuantityNormalized: item.blockedQuantity
                ? parseInt(item.blockedQuantity, 10)
                : 0,
              totalQuantityNormalized: item.totalQuantity ? parseInt(item.totalQuantity, 10) : 0,
              grossValueNormalized: Utils.Mask.Money(item.grossValue),
              maturityDateNormalized: Utils.Mask.DateFormat(item.maturityDate, 'dd/MM/yyyy'),
              maturityDaysRemainingNormalized: item.maturityDaysRemaining || '',
              purchaseDateNormalized: Utils.Mask.DateFormat(item.purchaseDate, 'dd/MM/yyyy'),
              purchaseDaysRemainingNormalized: item.purchaseDaysRemaining || '',
              iofValueNormalized: Utils.Mask.Money(item.iofValue),
              irValueNormalized: Utils.Mask.Money(item.irValue),
              purchaseValueNormalized: Utils.Mask.Money(item.purchaseValue),
              netValueNormalized: Utils.Mask.Money(item.netValue),
              formattedIndexRateNormalized: item.formattedIndexRate || '',
            } as FixedIncomeProductsQueryItem)
        );

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const fixedIncomeCategoriesQuery = selectorFamily<
  | {
      data: ReturnType<typeof normalizeFilters>;
    }
  | undefined,
  { year: number }
>({
  key: 'ManagementAnalysisDetails/fixedIncomeCategoriesQuery',
  get: () => async () => {
    const { data } = await Service.ReportingServices.V1.getReportingProductListingFilterParams({
      screenName,
      filtersParams: ['FixedIncome'],
    });

    const normalizedData = normalizeFilters(normalizeFilterResponse(data));

    return { data: normalizedData };
  },
});

export type FundsProductsQueryItem = Utils.ArrayElement<
  NonNullable<
    Awaited<
      ReturnType<typeof Service.ReportingServices.V1.getReportingProductListingInvestmentFunds>
    >['data']
  >
> & {
  color: ColorTypes.Option;
  customerNormalized: string;
  assetNormalized: string;
  quoteDateNormalized: string;
  quoteAnualValueNormalized: string;
  quoteQuantityNormalized: number;
  grossValueNormalized: string;
  netValueNormalized: string;
};

export type FundsProductsQueryData = AbstractProductsQueryData<FundsProductsQueryItem>;

export const fundsProductsQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: FundsProductsQueryData;
    }
  | undefined,
  { page: number; searchTerm?: string; year: number; filtersToApply: FiltersToApplyProps }
>({
  key: 'ManagementAnalysisDetails/fundsProductsQuery',
  get:
    ({ page, searchTerm, filtersToApply, year }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } =
          await Service.ReportingServices.V1.getReportingProductListingInvestmentFunds({
            screenName,
            page,
            searchTerm,
            year,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            ...filtersToApply,
          });

        const normalizedData = data?.map(
          (item) =>
            ({
              ...item,
              color: 'product.fundos',
              customerNormalized: item.customer || '',
              assetNormalized: item.asset || '',
              quoteDateNormalized: Utils.Mask.DateFormat(item.quoteDate, 'dd/MM/yyyy'),
              quoteAnualValueNormalized: Utils.Mask.Money(item.currentQuoteValue),
              quoteQuantityNormalized: item.units,
              grossValueNormalized: Utils.Mask.Money(item.grossValue),
              netValueNormalized: Utils.Mask.Money(item.netValue),
            } as FundsProductsQueryItem)
        );

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const fundsCategoriesQuery = selectorFamily<
  | {
      data: ReturnType<typeof normalizeFilters>;
    }
  | undefined,
  { year: number }
>({
  key: 'ManagementAnalysisDetails/fundsCategoriesQuery',
  get: () => async () => {
    const { data } = await Service.ReportingServices.V1.getReportingProductListingFilterParams({
      screenName,
      filtersParams: ['InvestmentFunds'],
    });

    const normalizedData = normalizeFilters(normalizeFilterResponse(data));

    return { data: normalizedData };
  },
});

export type DirectTreasuryProductsQueryItem = Utils.ArrayElement<
  NonNullable<
    Awaited<
      ReturnType<typeof Service.ReportingServices.V1.getReportingProductListingTreasuryDirect>
    >['data']
  >
> & {
  color: ColorTypes.Option;
  customerNormalized: string;
  productNameNormalized: string;
  positionDateNormalized: string;
  maturityDateNormalized: string;
  positionValueNormalized: string;
  assetNormalized: string;
};
export type DirectTreasuryProductsQueryData =
  AbstractProductsQueryData<DirectTreasuryProductsQueryItem>;

export const directTreasuryProductsQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: DirectTreasuryProductsQueryData;
    }
  | undefined,
  { page: number; searchTerm?: string; year: number; filtersToApply: FiltersToApplyProps }
>({
  key: 'ManagementAnalysisDetails/treasuryProductsQuery',
  get:
    ({ page, searchTerm, filtersToApply, year }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } =
          await Service.ReportingServices.V1.getReportingProductListingTreasuryDirect({
            screenName,
            page,
            searchTerm,
            year,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            ...filtersToApply,
          });

        const normalizedData = data?.map(
          (item) =>
            ({
              ...item,
              color: 'product.tesouro',
              customerNormalized: item.customer || '',
              productNameNormalized: item.productName || '',
              positionDateNormalized: Utils.Mask.DateFormat(item.positionDate, 'dd/MM/yyyy'),
              maturityDateNormalized: Utils.Mask.DateFormat(item.maturityDate, 'dd/MM/yyyy'),
              positionValueNormalized: Utils.Mask.Money(item.positionValue),
              assetNormalized: item.asset || '',
            } as DirectTreasuryProductsQueryItem)
        );

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export type EquityProductsQueryItemReceived = Utils.ArrayElement<
  NonNullable<
    Awaited<
      ReturnType<typeof Service.ReportingServices.V1.getReportingProductListingEquity>
    >['data']
  >
>;

export type EquityProductsQueryItem = EquityProductsQueryItemReceived & {
  color: ColorTypes.Option;
  customerNormalized: string;
  assetNameNormalized: string;
  availableQuantityNormalized: string;
  unavailableQuantityNormalized: string;
  currentPriceNormalized: string;
  averagePriceNormalized: string;
  valueNormalized: string;
  paidCustodyInfo: {
    paidCustodyAvailable: boolean;
    donatedQuantity: string;
    receivedQuantity: string;
  };
};

export type EquityProductsQueryData = AbstractProductsQueryData<EquityProductsQueryItem>;

export const equityProductsQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: EquityProductsQueryData;
    }
  | undefined,
  { page: number; searchTerm?: string; year: number; filtersToApply: FiltersToApplyProps }
>({
  key: 'ManagementAnalysisDetails/equityProductsQuery',
  get:
    ({ page, searchTerm, filtersToApply, year }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } = await Service.ReportingServices.V1.getReportingProductListingEquity({
          screenName,
          page,
          searchTerm,
          year,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          ...filtersToApply,
        });

        const normalizePaidCustodyInfo = (item: EquityProductsQueryItemReceived) => {
          if (item.hasBTC) {
            return {
              paidCustodyAvailable: true,
              donatedQuantity: (item.totalQuantityBorrowed || 0).toString(),
              receivedQuantity: (item.totalQuantityLent || 0).toString(),
            };
          }

          return {
            paidCustodyAvailable: false,
            donatedQuantity: '--',
            receivedQuantity: '--',
          };
        };

        const normalizedData = data?.map(
          (item) =>
            ({
              ...item,
              color: 'product.rendavariavel',
              customerNormalized: item.customer || '',
              assetNameNormalized: item.asset || '',
              availableQuantityNormalized: (item.quantityAvailable || 0).toString(),
              unavailableQuantityNormalized: (item.quantityBlocked || 0).toString(),
              currentPriceNormalized: Utils.Mask.Money(item.currentPrice),
              averagePriceNormalized: Utils.Mask.Money(item.currentAveragePrice),
              valueNormalized: Utils.Mask.Money(item.positionValue),
              paidCustodyInfo: normalizePaidCustodyInfo(item),
            } as EquityProductsQueryItem)
        );

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export type PrivatePensionProductsQueryItem = Utils.ArrayElement<
  NonNullable<
    Awaited<
      ReturnType<typeof Service.ReportingServices.V1.getReportingProductListingPrivatePension>
    >['data']
  >
> & {
  color: ColorTypes.Option;
  customerNormalized: string;
  assetNormalized: string;
  fundCNPJNormalized: string;
  classificationNormalized: string;
  taxNormalized: string;
  participantNormalized: string;
  planNormalized: string;
  positionDateNormalized: string;
  grossValueNormalized: string;
};

export type PrivatePensionProductsQueryData =
  AbstractProductsQueryData<PrivatePensionProductsQueryItem>;

export const privatePensionProductsQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: PrivatePensionProductsQueryData;
    }
  | undefined,
  { page: number; searchTerm?: string; year: number; filtersToApply: FiltersToApplyProps }
>({
  key: 'ManagementAnalysisDetails/privatePensionProductsQuery',
  get:
    ({ page, searchTerm, filtersToApply, year }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } =
          await Service.ReportingServices.V1.getReportingProductListingPrivatePension({
            screenName,
            page,
            searchTerm,
            year,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            ...filtersToApply,
          });

        const normalizedData = data?.map(
          (item) =>
            ({
              ...item,
              color: 'product.previdencia',
              customerNormalized: item.customer || '',
              assetNormalized: item.asset || '',
              fundCNPJNormalized: item.fundCNPJ || '--',
              classificationNormalized: item.classification || '--',
              taxNormalized: item.tax || '--',
              participantNormalized: item.participant || '--',
              planNormalized: item.plan || '--',
              positionDateNormalized: Utils.Mask.DateFormat(item.positionDate, 'dd/MM/yyyy'),
              grossValueNormalized: Utils.Mask.Money(item.grossValue),
            } as PrivatePensionProductsQueryItem)
        );

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const privatePensionCategoriesQuery = selectorFamily<
  | {
      data: ReturnType<typeof normalizeFilters>;
    }
  | undefined,
  { year: number }
>({
  key: 'ManagementAnalysisDetails/privatePensionCategoriesQuery',
  get: () => async () => {
    const { data } = await Service.ReportingServices.V1.getReportingProductListingFilterParams({
      screenName,
      filtersParams: ['PrivatePension'],
    });

    const normalizedData = normalizeFilters(normalizeFilterResponse(data));

    return { data: normalizedData };
  },
});

export type ClubProductsQueryItem = Utils.ArrayElement<
  NonNullable<
    Awaited<ReturnType<typeof Service.ReportingServices.V1.getReportingProductListingClub>>['data']
  >
> & {
  color: ColorTypes.Option;
  customerNormalized: string;
  assetNormalized: string;
  currentQuoteValueNormalized: string;
  grossValueNormalized: string;
  positionDateNormalized: string;
  netValueNormalized: string;
};

export type ClubProductsQueryData = AbstractProductsQueryData<ClubProductsQueryItem>;

export const clubsProductsQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: ClubProductsQueryData;
    }
  | undefined,
  { page: number; searchTerm?: string; year: number; filtersToApply: FiltersToApplyProps }
>({
  key: 'ManagementAnalysisDetails/clubsProductsQuery',
  get:
    ({ page, searchTerm, filtersToApply, year }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } = await Service.ReportingServices.V1.getReportingProductListingClub({
          screenName,
          page,
          searchTerm,
          year,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          ...filtersToApply,
        });

        const normalizedData = data?.map(
          (item) =>
            ({
              ...item,
              color: 'product.clubes',
              customerNormalized: item.customer || '',
              assetNormalized: item.asset || '',
              currentQuoteValueNormalized: Utils.Mask.Money(item.currentQuoteValue),
              grossValueNormalized: Utils.Mask.Money(item.grossValue),
              positionDateNormalized: item.positionDate
                ? Utils.Mask.DateFormat(item.positionDate, 'dd/MM/yyyy')
                : '--',
              netValueNormalized: Utils.Mask.Money(item.netValue),
            } as ClubProductsQueryItem)
        );

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const clubsCategoriesQuery = selectorFamily<
  | {
      data: ReturnType<typeof normalizeFilters>;
    }
  | undefined,
  { year: number }
>({
  key: 'ManagementAnalysisDetails/clubesCategoriesQuery',
  get: () => async () => {
    const { data } = await Service.ReportingServices.V1.getReportingProductListingFilterParams({
      screenName,
      filtersParams: ['Clubs'],
    });

    const normalizedData = normalizeFilters(normalizeFilterResponse(data));

    return { data: normalizedData };
  },
});

export type CoesFixedIncomeProductsQueryItem = Utils.ArrayElement<
  NonNullable<
    Awaited<
      ReturnType<typeof Service.ReportingServices.V1.getReportingProductListingCoeFixedIncome>
    >['data']
  >
> & {
  color: ColorTypes.Option;
  customerNormalized: string;
  issuerNormalized: string;
  productNameNormalized: string;
  indexNormalized: string;
  blockedQuantityNormalized: number;
  totalQuantityNormalized: number;
  grossValueNormalized: string;
  assetNormalized: string;
  positionDateNormalized: string;
  maturityDateNormalized: string;
  maturityDaysRemainingNormalized: string;
  purchaseDateNormalized: string;
  purchaseDaysRemainingNormalized: string;
  iofValueNormalized: string;
  irValueNormalized: string;
  purchaseValueNormalized: string;
  netValueNormalized: string;
  formattedIndexRateNormalized: string;
};

export type CoesFixedIncomeProductsQueryData =
  AbstractProductsQueryData<CoesFixedIncomeProductsQueryItem>;

export const coesFixedIncomeProductsQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: CoesFixedIncomeProductsQueryData;
    }
  | undefined,
  { page: number; searchTerm?: string; year: number; filtersToApply: FiltersToApplyProps }
>({
  key: 'ManagementAnalysisDetails/coesFixedIncomeProductsQuery',
  get:
    ({ page, searchTerm, filtersToApply, year }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } =
          await Service.ReportingServices.V1.getReportingProductListingCoeFixedIncome({
            screenName,
            page,
            searchTerm,
            year,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            ...filtersToApply,
          });

        const normalizedData = data?.map(
          (item) =>
            ({
              ...item,
              color: 'product.estruturados',
              customerNormalized: item.customer || '',
              issuerNormalized: item.issuer || '',
              productNameNormalized: item.productName || '',
              assetNormalized: item.asset || '',
              positionDateNormalized: Utils.Mask.DateFormat(item.positionDate, 'dd/MM/yyyy'),
              indexNormalized: item.index || '',
              blockedQuantityNormalized: item.blockedQuantity
                ? parseInt(item.blockedQuantity, 10)
                : 0,
              totalQuantityNormalized: item.totalQuantity ? parseInt(item.totalQuantity, 10) : 0,
              grossValueNormalized: Utils.Mask.Money(item.grossValue),
              maturityDateNormalized: Utils.Mask.DateFormat(item.maturityDate, 'dd/MM/yyyy'),
              maturityDaysRemainingNormalized: item.maturityDaysRemaining || '',
              purchaseDateNormalized: Utils.Mask.DateFormat(item.purchaseDate, 'dd/MM/yyyy'),
              purchaseDaysRemainingNormalized: item.purchaseDaysRemaining || '',
              iofValueNormalized: Utils.Mask.Money(item.iofValue),
              irValueNormalized: Utils.Mask.Money(item.irValue),
              purchaseValueNormalized: Utils.Mask.Money(item.purchaseValue),
              netValueNormalized: Utils.Mask.Money(item.netValue),
              formattedIndexRateNormalized: item.formattedIndexRate || '',
            } as FixedIncomeProductsQueryItem)
        );

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export type CoesEquityProductsQueryItem = Utils.ArrayElement<
  NonNullable<
    Awaited<
      ReturnType<typeof Service.ReportingServices.V1.getReportingProductListingCoeEquity>
    >['data']
  >
> & {
  color: ColorTypes.Option;
  customerNameNormalized: string;
  assetNameNormalized: string;
  startDateNormalized: string;
  maturityDateNormalized: string;
  appliedValueNormalized: string;
  currentValueNormalized: string;
  markValueNormalized: string;
  returnFormatted: string;
  returnNormalized: number;
};

export type CoesEquityProductsQueryData = AbstractProductsQueryData<CoesEquityProductsQueryItem>;

export const coesEquityProductsQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: CoesEquityProductsQueryData;
    }
  | undefined,
  { page: number; searchTerm?: string; year: number; filtersToApply: FiltersToApplyProps }
>({
  key: 'ManagementAnalysisDetails/coesEquityProductsQuery',
  get:
    ({ page, searchTerm, filtersToApply, year }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } =
          await Service.ReportingServices.V1.getReportingProductListingCoeEquity({
            screenName,
            page,
            searchTerm,
            year,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            ...filtersToApply,
          });

        const normalizedData = data?.map(
          (item) =>
            ({
              ...item,
              color: 'product.estruturados',
              customerNameNormalized: item.customer || '',
              assetNameNormalized: item.productName || '',
              startDateNormalized: Utils.Mask.DateFormat(item.beginDate, 'dd/MM/yyyy'),
              maturityDateNormalized: Utils.Mask.DateFormat(item.dueDate, 'dd/MM/yyyy'),
              appliedValueNormalized: Utils.Mask.Money(item.appliedValue),
              currentValueNormalized: Utils.Mask.Money(item.currentValue),
              markValueNormalized: Utils.Mask.Money(item.marketValue),
              returnFormatted: Utils.Mask.Percentage(item.payback),
              returnNormalized: item.payback,
            } as CoesEquityProductsQueryItem)
        );

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export type CheckingAccountQueryItem = Utils.ArrayElement<
  NonNullable<
    Awaited<ReturnType<typeof Service.ReportingServices.V1.getReportingBalanceListing>>['data']
  >
>['balance'] & {
  color: ColorTypes.Option;
  customerNameNormalized: string;
  advisorNameNormalized: string;
  d1ValueNormalized: string;
  d2ValueNormalized: string;
  d3ValueNormalized: string;
  dnValueNormalized: string;
  availableValueNormalized: string;
  projectedValueNormalized: string;
  totalValueNormalized: string;
};

export type CheckingAccountQueryData = AbstractProductsQueryData<CheckingAccountQueryItem>;

export const checkingAccountProductsQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: CheckingAccountQueryData;
    }
  | undefined,
  { page: number; searchTerm?: string; filtersToApply: FiltersToApplyProps }
>({
  key: 'ManagementAnalysisDetails/checkingAccountProductsQuery',
  get:
    ({ page, searchTerm, filtersToApply }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } = await Service.ReportingServices.V1.getReportingBalanceListing({
          screenName,
          page,
          searchTerm,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          ...filtersToApply,
        });

        const normalizedData = data?.map(
          ({ balance: item }) =>
            ({
              ...item,
              color: 'product.contacorrente',
              customerNameNormalized: item.customerName || '',
              advisorNameNormalized: item.advisorName || '',
              d1ValueNormalized: Utils.Mask.Money(item.d1Value),
              d2ValueNormalized: Utils.Mask.Money(item.d2Value),
              d3ValueNormalized: Utils.Mask.Money(item.d3Value),
              dnValueNormalized: Utils.Mask.Money(item.dnValue),
              availableValueNormalized: Utils.Mask.Money(item.availableValue),
              projectedValueNormalized: Utils.Mask.Money(item.projectedValue),
              totalValueNormalized: Utils.Mask.Money(item.totalValue),
            } as CheckingAccountQueryItem)
        );

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

type EmptyCategoriesQueryParams = undefined;

export const emptyCategoriesQuery = selectorFamily<
  | {
      data: ReturnType<typeof normalizeFilters>;
    }
  | undefined,
  EmptyCategoriesQueryParams
>({
  key: 'ManagementAnalysisDetails/emptyCategoriesQuery',
  get: () => async () => ({ data: [] }),
});
